import { create } from 'zustand'

// 三种主要模式
export type ViewMode = 'FORMED' | 'CHAOS' | 'GALLERY'
export type TreeState = 'CHAOS' | 'FORMED' | 'GALLERY' | 'TRANSITIONING'

// 手势类型
export type GestureType = 'NONE' | 'ONE_HAND_OPEN' | 'ONE_HAND_CLOSED' | 'TWO_HANDS_OPEN' | 'TWO_HANDS_CLOSED'

interface TreeStore {
  // 状态
  treeState: TreeState
  targetState: ViewMode
  progress: number // 0 = 完全混沌, 1 = 完全成形
  galleryProgress: number // 0 = 展开状态, 1 = 画廊模式
  isHandTracking: boolean
  isHandOpen: boolean
  handCount: number // 检测到的手的数量
  gestureType: GestureType // 当前手势类型
  cameraRotation: { x: number; y: number }
  treeRotationY: number // 圣诞树Y轴旋转角度
  sceneRotationY: number // 展开状态下整个场景的旋转
  galleryRotationY: number // 画廊模式下的旋转
  selectedPhotoIndex: number | null // 选中的照片索引
  photoScale: number // 照片缩放比例
  focusPhotoIndex: number // 画廊模式下当前焦点照片索引
  
  // 动作
  setTreeState: (state: TreeState) => void
  setTargetState: (state: ViewMode) => void
  setProgress: (progress: number) => void
  setGalleryProgress: (progress: number) => void
  setIsHandTracking: (isTracking: boolean) => void
  setIsHandOpen: (isOpen: boolean) => void
  setHandCount: (count: number) => void
  setGestureType: (gesture: GestureType) => void
  setCameraRotation: (rotation: { x: number; y: number }) => void
  setTreeRotationY: (delta: number) => void // 增量旋转
  setSceneRotationY: (delta: number) => void // 展开状态下场景旋转
  setGalleryRotationY: (delta: number) => void // 画廊模式旋转
  resetSceneRotation: () => void // 重置场景旋转到正面
  setSelectedPhotoIndex: (index: number | null) => void
  setPhotoScale: (scale: number) => void
  setFocusPhotoIndex: (index: number) => void // 设置焦点照片
  nextPhoto: () => void // 下一张照片
  prevPhoto: () => void // 上一张照片
  toggleState: () => void
  
  // 手势状态更新（综合处理）
  updateFromGesture: (handCount: number, handsOpen: boolean[]) => void
}

export const useTreeStore = create<TreeStore>((set, get) => ({
  // 初始状态
  treeState: 'FORMED',
  targetState: 'FORMED',
  progress: 1,
  galleryProgress: 0,
  isHandTracking: false,
  isHandOpen: false,
  handCount: 0,
  gestureType: 'NONE',
  cameraRotation: { x: 0, y: 0 },
  treeRotationY: 0,
  sceneRotationY: 0,
  galleryRotationY: 0,
  selectedPhotoIndex: null,
  photoScale: 1,
  focusPhotoIndex: 0,
  
  // 动作实现
  setTreeState: (state) => set({ treeState: state }),
  
  setTargetState: (state) => set({ targetState: state }),
  
  setProgress: (progress) => set({ progress: Math.max(0, Math.min(1, progress)) }),
  
  setGalleryProgress: (progress) => set({ galleryProgress: Math.max(0, Math.min(1, progress)) }),
  
  setIsHandTracking: (isTracking) => set({ isHandTracking: isTracking }),
  
  setIsHandOpen: (isOpen) => set({ isHandOpen: isOpen }),
  
  setHandCount: (count) => set({ handCount: count }),
  
  setGestureType: (gesture) => set({ gestureType: gesture }),
  
  setCameraRotation: (rotation) => set({ cameraRotation: rotation }),
  
  // 增量旋转圣诞树
  setTreeRotationY: (delta) => set((state) => ({
    treeRotationY: state.treeRotationY + delta
  })),
  
  // 展开状态下场景旋转
  setSceneRotationY: (delta) => set((state) => ({
    sceneRotationY: state.sceneRotationY + delta
  })),
  
  // 画廊模式旋转
  setGalleryRotationY: (delta) => set((state) => ({
    galleryRotationY: state.galleryRotationY + delta
  })),
  
  // 重置场景旋转到正面（画廊模式进入时调用）
  resetSceneRotation: () => set({
    sceneRotationY: 0,
    treeRotationY: 0,
    galleryRotationY: 0
  }),
  
  setSelectedPhotoIndex: (index) => set({ selectedPhotoIndex: index }),
  
  setPhotoScale: (scale) => set({ photoScale: Math.max(0.5, Math.min(3, scale)) }),
  
  setFocusPhotoIndex: (index) => set({ focusPhotoIndex: index }),
  
  nextPhoto: () => set((state) => ({
    focusPhotoIndex: (state.focusPhotoIndex + 1) % 8 // 8张照片
  })),
  
  prevPhoto: () => set((state) => ({
    focusPhotoIndex: (state.focusPhotoIndex - 1 + 8) % 8 // 8张照片
  })),
  
  toggleState: () => {
    const currentState = get().targetState
    if (currentState === 'FORMED') {
      set({ targetState: 'CHAOS' })
    } else if (currentState === 'CHAOS') {
      set({ targetState: 'FORMED' })
    } else if (currentState === 'GALLERY') {
      set({ targetState: 'CHAOS' })
    }
  },
  
  // 综合手势更新
  updateFromGesture: (handCount: number, handsOpen: boolean[]) => {
    const { targetState } = get()
    
    set({ handCount })
    
    // 确定手势类型
    let gestureType: GestureType = 'NONE'
    if (handCount === 0) {
      gestureType = 'NONE'
    } else if (handCount === 1) {
      gestureType = handsOpen[0] ? 'ONE_HAND_OPEN' : 'ONE_HAND_CLOSED'
    } else if (handCount >= 2) {
      const bothOpen = handsOpen[0] && handsOpen[1]
      const bothClosed = !handsOpen[0] && !handsOpen[1]
      if (bothOpen) {
        gestureType = 'TWO_HANDS_OPEN'
      } else if (bothClosed) {
        gestureType = 'TWO_HANDS_CLOSED'
      } else {
        // 一只手张开一只手合上，保持当前状态
        gestureType = get().gestureType
      }
    }
    
    set({ gestureType })
    
    // 画廊模式下的特殊处理：
    // - 只检测双手合上（退出画廊到展开模式）
    // - 单手滑动切换照片（在 HandGestureProvider 中处理）
    // - 不检测单手合上/展开，防止直接跳到圣诞树模式
    if (targetState === 'GALLERY') {
      // 画廊模式下只响应双手合上
      if (gestureType === 'TWO_HANDS_CLOSED') {
        // 双手合上 → 退出画廊到展开状态（CHAOS），禁止直接到圣诞树
        set({ targetState: 'CHAOS', isHandOpen: false })
      }
      // 其他手势在画廊模式下不改变状态，只用于滑动切换照片
      return
    }
    
    // 非画廊模式下的正常手势处理
    // 规则：
    // 1. 单手张开 → 展开状态 (CHAOS)
    // 2. 单手合上 → 圣诞树状态 (FORMED)
    // 3. 双手张开 → 画廊模式 (GALLERY)，但只能从展开状态进入
    // 4. 双手合上 → 展开状态 (CHAOS)
    
    if (gestureType === 'ONE_HAND_OPEN') {
      // 单手张开 → 展开状态
      if (targetState === 'FORMED') {
        set({ targetState: 'CHAOS', isHandOpen: true })
      } else {
        set({ isHandOpen: true })
      }
    } else if (gestureType === 'ONE_HAND_CLOSED') {
      // 单手合上 → 圣诞树状态
      if (targetState === 'CHAOS') {
        set({ targetState: 'FORMED', isHandOpen: false })
      } else {
        set({ isHandOpen: false })
      }
    } else if (gestureType === 'TWO_HANDS_OPEN') {
      // 双手张开 → 画廊模式（只能从展开状态进入）
      if (targetState === 'CHAOS') {
        // 进入画廊模式时重置旋转，确保视角正对照片
        set({
          targetState: 'GALLERY',
          isHandOpen: true,
          sceneRotationY: 0,
          treeRotationY: 0,
          galleryRotationY: 0
        })
      }
      // 从圣诞树状态不能直接进入画廊
    } else if (gestureType === 'TWO_HANDS_CLOSED') {
      // 双手合上 → 展开状态
      if (targetState === 'FORMED') {
        set({ targetState: 'CHAOS', isHandOpen: false })
      }
    }
  },
}))